import { useState, useEffect, useRef } from "react";
import "./ApplicationForm.css";

function ApplicationForm({
  title = "Arrange a Private Viewing",
  description = "Your First Step Toward Exceptional Living. Don’t Miss the Opportunity to Explore Limited Availability Units.",
  unitLayouts = [],
  bgColor = "",
  primaryColor = "",
  secondaryColor = "",
  tertiaryColor = "",
  accentColor = "",
}) {
  const titleRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    mobileNo: "",
    email: "",
    unitType: "Type A",
    viewingDateTime: "",
    remark: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: false }));
  };

  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = true;

    if (!form.mobileNo.trim()) {
      newErrors.mobileNo = true;
    } else {
      const mobileRegex = /^[0-9+\-\s]*$/;
      if (!mobileRegex.test(form.mobileNo)) newErrors.mobileNo = true;
    }

    if (!form.email.trim()) newErrors.email = true;

    // ✅ Optional viewingDateTime validation
    if (form.viewingDateTime) {
      const selected = new Date(form.viewingDateTime);
      const now = new Date();

      // ❌ Prevent past datetime
      if (selected < now) {
        newErrors.viewingDateTime = true;
      } else {
        const hour = selected.getHours();

        // ❌ Allow only 10AM–6PM
        if (hour < 10 || hour >= 18) {
          newErrors.viewingDateTime = true;
          alert("Viewing available daily from 10:00 AM – 6:00 PM")
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatMalaysiaDateTime = (dateValue) => {
    if (!dateValue) return null;

    const date = new Date(dateValue);

    const malaysiaDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" })
    );

    const pad = (num) => String(num).padStart(2, "0");

    const year = malaysiaDate.getFullYear();
    const month = pad(malaysiaDate.getMonth() + 1);
    const day = pad(malaysiaDate.getDate());

    let hours = malaysiaDate.getHours();
    const minutes = pad(malaysiaDate.getMinutes());
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    if (hours === 0) hours = 12;
    hours = pad(hours);

    return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
  };
  const notifyMe = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        ...form,
        viewingDateTime: form.viewingDateTime
          ? formatMalaysiaDateTime(form.viewingDateTime)
          : "-",
        remark: form.remark && form.remark.trim()
          ? form.remark
          : "-",
      };

      console.log(formatMalaysiaDateTime(form.viewingDateTime))

      const res = await fetch("https://notify-kbixl6rtpq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Request failed");

      if (window.gtag_report_conversion) {
        window.gtag_report_conversion();
      }

      alert("Viewing appointment submitted!");

      setForm({
        name: "",
        mobileNo: "",
        email: "",
        unitType: "Type A",
        viewingDateTime: "",
        remark: "",
      });
    } catch (err) {
      console.error(err);
      alert("Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Animate section title
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("section-title-visible");
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    if (titleRef.current) observer.observe(titleRef.current);

    return () => observer.disconnect();
  }, []);

  // ✅ Prevent selecting past datetime in UI
  const now = new Date();
  const minDateTime = new Date(
    now.getTime() - now.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 16);

  const hexToRgb = (hex) => {
    let cleanHex = hex.replace("#", "");
    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split("").map(c => c + c).join("");
    }
    const bigint = parseInt(cleanHex, 16);
    return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
  }

  return (
    <div
      id="application-form-section"
      className="form-section"
      style={{
        "--accent-color": accentColor,
        "--accent-color-rgb": hexToRgb(accentColor),
        "--primary-color": primaryColor,
        "--secondary-color": secondaryColor,
        "--tertiary-color": tertiaryColor,
        "--bg-color": bgColor,
      }}>
      <div className="form-section-title" ref={titleRef}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div className="application-form">
        <h2 className="form-title">
          Register Your Interest
        </h2>

        <div className="form-field">
          <label>
            Full Name <span>*</span>
          </label>
          <input
            className={`form-input ${errors.name ? "form-input-error" : ""}`}
            name="name"
            placeholder="Enter your full name"
            value={form.name}
            onChange={handleChange}
          />
        </div>

        <div className="form-field">
          <label>
            Contact Number <span>*</span>
          </label>
          <input
            className={`form-input ${errors.mobileNo ? "form-input-error" : ""}`}
            name="mobileNo"
            placeholder="+6012-345 6789"
            value={form.mobileNo}
            onChange={handleChange}
          />
        </div>

        <div className="form-field">
          <label>
            Email Address <span>*</span>
          </label>
          <input
            className={`form-input ${errors.email ? "form-input-error" : ""}`}
            name="email"
            type="email"
            placeholder="your.email@example.com"
            value={form.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-field">
          <label>
            Interested Unit Type <span>*</span>
          </label>
          <select
            className="form-input"
            name="unitType"
            value={form.unitType}
            onChange={handleChange}
          >
            {unitLayouts.map((layout, index) => (
              <option key={index} value={layout.unitType}>
                {layout.unitType}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>
            Preferred Viewing Date & Time{" "}
            <span className="label-optional">(Optional)</span>
          </label>
          <input
            className={`form-input ${errors.viewingDateTime ? "form-input-error" : ""
              }`}
            type="datetime-local"
            name="viewingDateTime"
            value={form.viewingDateTime}
            min={minDateTime}
            onChange={handleChange}
          />
          <small className="form-hint">
            Viewing available daily from 10:00 AM – 6:00 PM
          </small>
        </div>

        <div className="form-field">
          <label>
            Remark <span className="label-optional">(Optional)</span>
          </label>
          <textarea
            className="form-textarea"
            name="remark"
            placeholder="Any specific requirements or questions..."
            value={form.remark}
            onChange={handleChange}
          />
        </div>

        <button
          className="submit-button"
          onClick={notifyMe}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Registration"}
        </button>

        <p className="form-footer">
          By submitting, you agree to receive updates. Your information is
          secure and will not be shared.
        </p>
      </div>
    </div>
  );
}

export default ApplicationForm;